"use client";

import { useQuery } from "@tanstack/react-query";

// Tuple: [totalVolumeUsd, yesShare0to1]
export type VolumeBar = [number, number];

async function fetchVolumeBulk(cids: string[]): Promise<Map<string, VolumeBar[]>> {
  if (cids.length === 0) return new Map();
  const qs = new URLSearchParams({ cids: cids.join(",") });
  const res = await fetch(`/api/analytics/markets/volume-bulk?${qs.toString()}`);
  if (!res.ok) return new Map();
  const json = await res.json();
  const data = (json?.data || {}) as Record<string, VolumeBar[]>;
  const map = new Map<string, VolumeBar[]>();
  for (const [k, v] of Object.entries(data)) {
    if (Array.isArray(v) && v.length >= 2) {
      map.set(k.toLowerCase(), v);
    }
  }
  return map;
}

/**
 * Bulk 24h volume histogram (hourly) — one fetch for the whole list view.
 * Each entry is an array of [totalVolume, yesShare] tuples.
 */
export function useVolumeHistoryMap(conditionIds: string[], enabled = true) {
  const key = Array.from(new Set(conditionIds.map((c) => c.toLowerCase()))).sort().join(",");
  return useQuery<Map<string, VolumeBar[]>>({
    queryKey: ["analytics", "volume-bulk", key],
    queryFn: () => fetchVolumeBulk(conditionIds),
    // Skip under HIDE_PREDEXON — volume-bulk is empty during the Predexon
    // outage, so the volume bars hide instead of rendering blank.
    enabled: enabled && conditionIds.length > 0 && process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS !== "true",
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: false,
  });
}
